// AppHeader.tsx
import { Menu, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import BannerImage from '../assets/location-station-banner.svg';
import AudioPlayer from './AudioPlayer';
import UserProfile from './UserProfile';
import RefreshButton from './widgets/RefreshButton';
import axios from 'axios';
import { HlsAudioPlayer } from '../utils/HlsAudioPlayer';
import sseConsumer, { EventData, StreamEvent } from '../utils/ServerSideEventConsumer';

interface AppHeaderProps {
  newUserHlsPlayer: HlsAudioPlayer;
}

const AppHeader: React.FC<AppHeaderProps> = ({ newUserHlsPlayer }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [hlsAudioPlayer] = useState<HlsAudioPlayer>(new HlsAudioPlayer());

  useEffect(() => {
    return () => {
    };
  }, []);

  const refresh = async () => {
    const segment = 'all';
    await axios.post(`${process.env.REACT_APP_API_URL}/refresh`,
      { segment },
      {
        withCredentials: true,
        params: {
          forceUser: JSON.parse(process.env.REACT_APP_USER || '{}')?.email,
        }
      }
    )
  }

  const requestHls = useCallback(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/user/hls`, {
      withCredentials: true,
      params: {
        forceUser: JSON.parse(process.env.REACT_APP_USER || '{}')?.email,
      }
    }).catch((error) => {
      console.error('Error fetching hls url:', error);
    });
  }, []);

  const streamReady = useCallback(async (streamEvent: EventData) => {
    if (streamEvent.eventType === 'stream-ready') {
      const streamUrl = `${process.env.REACT_APP_MEDIA_URL}/${(streamEvent as StreamEvent).hlsStreamUrl}`;
      try {
        newUserHlsPlayer.fadeOut(2 * 1000).then(() => {
          newUserHlsPlayer.destroy();
        })
        await hlsAudioPlayer.fadeIn(streamUrl, 2 * 1000);
      } catch (error) {
        console.error('Playback failed:', error);
      }
    }
  }, [hlsAudioPlayer, newUserHlsPlayer]);

  useEffect(() => {
    sseConsumer.registerCallback("stream-ready", streamReady);
    sseConsumer.registerCallback("open", requestHls);

    return () => {
      sseConsumer.removeCallback("stream-ready", streamReady);
      sseConsumer.removeCallback("open", requestHls);

      hlsAudioPlayer.destroy();
    }
  }, [hlsAudioPlayer, requestHls, streamReady]);


  return (
    <header className="fixed top-0 w-full bg-white shadow-sm z-1000">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-full">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <img src={BannerImage} alt="Location Station" className="mr-2 h-12 w-auto" />
          </div>

          {/* Search Box - Center */}
          {/* <div className="hidden md:block flex-1 max-w-2xl mx-4">
            <SearchBox />
          </div> */}

          {/* Audio Player and User Profile */}
          <div className="flex items-center space-x-4">
            <div className="hidden md:block w-64">
              <RefreshButton refresh={refresh} hlsAudioPlayer={hlsAudioPlayer} />
            </div>
            <div className="hidden md:block w-64">
              <AudioPlayer hlsAudioPlayer={hlsAudioPlayer} />
            </div>
            <UserProfile
              isSettingsOpen={isSettingsOpen}
              setSettingsOpen={setIsSettingsOpen}
              isMenuOpen={isMenuOpen}
              setMenuOpen={setIsMenuOpen}
            />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-gray-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

    </header>
  );
};

export default AppHeader;